import React, {Component} from "react"
import PropTypes from "prop-types"
import debounce from "debounce-promise"
import {get, isNil} from "lodash"

import {connect} from "@irati/store/connect"
import {hasValidValues} from "@irati/geolocation/src/helpers/latLng"

import {
  DEFAULT_DEBOUNCE_TYPING_TO_URL,
  DEFAULT_GEO_SEARCH_LIMIT
} from "../constants/search"
import {isSearchTermValid} from "../helpers/search/validation"
import addQueryUtil from "../utils/addQuery"
import {
  extractSearchTerm,
  extractLat,
  extractLng,
  extractRadius,
  extractBoundingBox
} from "../helpers/search/query"
import {hasDataChanged} from "../helpers/search/data"
import {convertBoundingBoxStringToBoundingBox} from "../helpers/search/location"
import {
  newSearch,
  retryLastSearch,
  pendLoadResults,
  clearResults,
  clearPend
} from "../store/actions/SearchActions"
import removeQuery from "../utils/removeQuery"

const withSearch = (WrappedComponent, isGeoSearch = false) => {
  class SearchClass extends Component {
    constructor(props) {
      super(props)

      this.state = {
        isValidSearchTerm: false,
        searchTerm: extractSearchTerm(),
        lat: extractLat(),
        lng: extractLng(),
        radius: extractRadius(),
        boundingBox: extractBoundingBox()
      }

      this.debounceUpdateQuery = debounce(addQueryUtil.addQuery, DEFAULT_DEBOUNCE_TYPING_TO_URL)
    }

    checkIfNewFetchIsNeeded = () => {
      const {fetchNewSearch} = this.props
      const currentValues = {q: extractSearchTerm()}
      if (isGeoSearch) {
        currentValues.lat = extractLat()
        currentValues.lng = extractLng()
        currentValues.radius = extractRadius()
        currentValues.boundingBox = extractBoundingBox()
      }
      const hasChanged = hasDataChanged(currentValues, get(this.props, "search", {}), {isGeoSearch, compareOnLastSuccess: false})
      const {q, lat, lng, radius, boundingBox} = currentValues
      const areValuesSet = hasValidValues(lat, lng) && !isNil(radius)
      if (isGeoSearch) {
        if ((areValuesSet || boundingBox) && hasChanged) {
          fetchNewSearch(q, {lat, lng, radius, boundingBox}, {limit: DEFAULT_GEO_SEARCH_LIMIT, fetchPolicy: "network-only"})
        }
      } else if (q && hasChanged) {
        fetchNewSearch(q, {fetchPolicy: "network-only"})
      }
    }

    UNSAFE_componentWillMount() {
      this.props.clearResults()
      const searchTerm = extractSearchTerm()
      /** Initial URL -> state sync */
      this.setSearchTermAndValidity(searchTerm)
      this.checkIfNewFetchIsNeeded()
    }

    componentWillUnmount() {
      this.props.clearResults()
    }

    componentDidUpdate() {
      this.checkIfNewFetchIsNeeded()
    }

    setSearchTermAndValidity = (searchTerm) => {
      this.setState({
        searchTerm,
        isValidSearchTerm: isSearchTermValid(searchTerm, !isGeoSearch)
      })
    }

    setLatLng = (lat, lng) => {
      this.setState({lat, lng})
    }

    setRadius = (radius) => {
      this.setState({radius})
    }

    setBoundingBox = (boundingBox) => {
      this.setState({boundingBox})
    }

    /**
     * dispatches input changes and syncs URL params
     * @param searchTerm
     */
    handleNewSearchTermEntry = (searchTerm) => {
      /** set state changes */
      this.setSearchTermAndValidity(searchTerm)

      /** Set user is typing / pend fetch */
      const lastSearchTerm = get(this.props, "search.q")
      if (searchTerm === lastSearchTerm) {
        this.props.clearPend()
      } else {
        this.props.pendFetchNewSearch()
      }

      /** Update the url */
      if (searchTerm || isGeoSearch) {
        this.debounceUpdateQuery({
          q: searchTerm
        })
      } else {
        removeQuery("q")
      }
    }

    /**
     * dispatches input changes and syncs URL params
     * @param lat
     * @param lng
     * @param radius
     */
    handleNewGeoEntryByRadius = (lat = null, lng = null, radius = null) => {
      const query = {}
      removeQuery("boundingBox")
      /** Update the url */
      const isLatLngSet = hasValidValues(lat, lng)
      const isRadiusSet = !isNil(radius)
      if (isLatLngSet) {
        query.lat = lat
        query.lng = lng
      } else {
        removeQuery("lat")
        removeQuery("lng")
      }

      if (isRadiusSet) {
        query.radius = radius
      } else {
        removeQuery("radius")
      }

      addQueryUtil.addQuery(query)
      if (isLatLngSet) {
        this.setLatLng(lat, lng)
      }
      if (isRadiusSet) {
        this.setRadius(radius)
      }
    }

    /**
     * dispatches input changes and syncs URL params
     * @param boundingBox
     */
    handleNewGeoEntryByBoundingBox = (boundingBox) => {
      const boundingBoxString = boundingBox.join(",")
      removeQuery("lat")
      removeQuery("lng")
      removeQuery("radius")
      addQueryUtil.addQuery({boundingBox: boundingBoxString})
      this.setBoundingBox(boundingBoxString)
    }

    render() {
      const geoProps = {}
      if (isGeoSearch) {
        geoProps.handleNewGeoEntryByRadius = this.handleNewGeoEntryByRadius
        geoProps.handleNewGeoEntryByBoundingBox = this.handleNewGeoEntryByBoundingBox
        const {lat, lng, radius, boundingBox} = this.state
        geoProps.lat = lat
        geoProps.lng = lng
        geoProps.radius = radius
        geoProps.boundingBox = convertBoundingBoxStringToBoundingBox(boundingBox)
      }

      return (
        <WrappedComponent
          {...this.props}
          {...geoProps}
          search={this.props.search}
          retryLastSearch={this.props.retryLastSearch}
          handleNewSearchTermEntry={this.handleNewSearchTermEntry}
          isValidSearchTerm={this.state.isValidSearchTerm}
          searchTerm={this.state.searchTerm} />
      )
    }
  }

  SearchClass.propTypes = {
    search: PropTypes.object.isRequired,
    fetchNewSearch: PropTypes.func.isRequired,
    pendFetchNewSearch: PropTypes.func.isRequired,
    retryLastSearch: PropTypes.func.isRequired,
    clearResults: PropTypes.func.isRequired
  }

  SearchClass.defaultProps = {
    search: {}
  }

  return connect(mapStateToProps, mapDispatchToProps)(SearchClass)
}

/**
 * Add Redux state & export
 */
export const mapStateToProps = ({search}) => ({search})

export const mapDispatchToProps = (dispatch) => {
  return {
    fetchNewSearch: (term, geoParams, options) => {
      return dispatch(newSearch(term, geoParams, options))
    },
    pendFetchNewSearch: () => {
      return dispatch(pendLoadResults())
    },
    retryLastSearch: () => {
      return dispatch(retryLastSearch())
    },
    clearResults: () => {
      return dispatch(clearResults())
    },
    clearPend: () => {
      return dispatch(clearPend())
    }
  }
}

export default withSearch
